/**
 * Défis live privés dans l'onglet Live School (migration 0650) : carte
 * « Organise ton propre live » (création) et défis organisés ou reçus encore à
 * jouer, en cartes cover qui défilent à l'horizontale.
 */

import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Radio } from 'lucide-react';
import { liveCoverUrl, presetPath } from '@/lib/live-covers';
import { privateLiveStage, useMyPrivateLives, type MyPrivateLive } from '@/lib/private-live';
import { syncPrivateLiveNotifications } from '@/lib/live-local-notifications';
import { PrivateLiveCard } from '@/components/news/private-live-card';
import { LiveCoverCard, liveCtaClass } from '@/features/live-ui/live-cover-card';
import { useNow } from '@/features/live-ui/use-now';

/** Libellé d'état d'un défi privé. PUR. */
export function privateLiveStatusLabel(live: MyPrivateLive, now: number = Date.now()): string {
  const stage = privateLiveStage(live, now);
  if (stage === 'finished') return 'Terminé';
  if (stage === 'running') return 'En cours';
  if (stage === 'awaiting') return live.hosted ? 'À lancer' : 'En attente du départ';
  return `Départ ${new Date(live.starts_at!).toLocaleString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/** Bouton seul (Feed prof / parent). */
export function CreatePrivateLiveLink({ className = '' }: { className?: string }) {
  return (
    <Link
      to="/live/prive/nouveau"
      className={`border-primary-200 text-primary-700 ocean:border-primary-400/40 ocean:text-primary-200 inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-semibold active:scale-[0.98] ${className}`}
    >
      <Radio className="h-4 w-4" aria-hidden />
      Créer un défi live privé
    </Link>
  );
}

export function PrivateLivesSection() {
  const { data = [] } = useMyPrivateLives();
  const now = useNow();
  const open = data.filter((l) => privateLiveStage(l, now) !== 'finished');

  // Rappel local 1 min avant chaque défi privé qui a une heure (no-op web).
  useEffect(() => {
    if (data.length === 0) return;
    void syncPrivateLiveNotifications(data);
  }, [data]);

  return (
    <section className="mb-6 space-y-4" aria-label="Défis privés">
      <LiveCoverCard
        size="md"
        badge="private"
        coverUrl={liveCoverUrl({ path: presetPath('duel-bleu') })}
        title="Organise ton propre live"
      >
        <p className="mt-1.5 text-sm text-white/85">
          Pour tes amis, ta classe ou ta famille : choisis les questions et la cover, partage le
          lien, lance le défi.
        </p>
        <Link to="/live/prive/nouveau" className={liveCtaClass('gold', 'mt-4')}>
          <Plus className="h-5 w-5" aria-hidden />
          Créer un défi privé
        </Link>
      </LiveCoverCard>

      {open.length > 0 && (
        <div>
          <h2 className="font-display text-ink-500 mb-2 text-xs font-bold uppercase tracking-widest">
            Mes défis privés
          </h2>
          <div className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
            {open.map((l) => (
              <PrivateLiveCard
                key={l.id}
                data={l}
                size="md"
                className="w-[85%] max-w-[22rem] shrink-0 snap-start"
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
